import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function SchemesManager() {
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [newBenefit, setNewBenefit] = useState('');
  const [formData, setFormData] = useState({
    icon: '',
    color: '#FF1493',
    title: '',
    ministry: '',
    benefits: [],
    eligibility: '',
    order: 1,
  });

  useEffect(() => {
    loadSchemes();
  }, []);

  const loadSchemes = async () => {
    try {
      const q = query(collection(db, 'schemes'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const schemesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchemes(schemesData);
    } catch (error) {
      console.error('Error loading schemes:', error);
      alert('Failed to load schemes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingScheme) {
        await updateDoc(doc(db, 'schemes', editingScheme.id), {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
        alert('Scheme updated successfully!');
      } else {
        await addDoc(collection(db, 'schemes'), {
          ...formData,
          createdAt: new Date().toISOString(),
        });
        alert('Scheme added successfully!');
      }
      setShowModal(false);
      setEditingScheme(null);
      resetForm();
      loadSchemes();
    } catch (error) {
      console.error('Error saving scheme:', error);
      alert('Failed to save scheme');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (scheme) => {
    setEditingScheme(scheme);
    setFormData({
      icon: scheme.icon,
      color: scheme.color,
      title: scheme.title,
      ministry: scheme.ministry,
      benefits: scheme.benefits || [],
      eligibility: scheme.eligibility,
      order: scheme.order,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this scheme?')) return;

    try {
      await deleteDoc(doc(db, 'schemes', id));
      alert('Scheme deleted successfully!');
      loadSchemes();
    } catch (error) {
      console.error('Error deleting scheme:', error);
      alert('Failed to delete scheme');
    }
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setFormData({
        ...formData,
        benefits: [...formData.benefits, newBenefit.trim()]
      });
      setNewBenefit('');
    }
  };

  const removeBenefit = (index) => {
    setFormData({
      ...formData,
      benefits: formData.benefits.filter((_, i) => i !== index)
    });
  };

  const resetForm = () => {
    setFormData({
      icon: '',
      color: '#FF1493',
      title: '',
      ministry: '',
      benefits: [],
      eligibility: '',
      order: 1,
    });
    setNewBenefit('');
  };

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Schemes Management</h2>
            <button
              onClick={() => {
                setEditingScheme(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Add New Scheme
            </button>
          </div>

          {loading && <p>Loading...</p>}

          {!loading && schemes.length === 0 && (
            <p>No schemes found. Add your first scheme!</p>
          )}

          {!loading && schemes.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Title</th>
                  <th>Ministry</th>
                  <th>Eligibility</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schemes.map((scheme) => (
                  <tr key={scheme.id}>
                    <td>{scheme.order}</td>
                    <td>{scheme.title}</td>
                    <td>{scheme.ministry}</td>
                    <td>{scheme.eligibility}</td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(scheme)}
                        className="btn btn-secondary"
                        style={{ marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(scheme.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingScheme ? 'Edit Scheme' : 'Add New Scheme'}
              </h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Icon Name *</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="e.g., school, wallet, gift"
                  required
                />
              </div>

              <div className="form-group">
                <label>Color *</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter scheme title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Ministry *</label>
                <input
                  type="text"
                  value={formData.ministry}
                  onChange={(e) => setFormData({ ...formData, ministry: e.target.value })}
                  placeholder="Enter ministry name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Benefits *</label>
                {formData.benefits.length > 0 && (
                  <ul className="benefits-list">
                    {formData.benefits.map((benefit, index) => (
                      <li key={index}>
                        <span style={{ flex: 1 }}>{benefit}</span>
                        <button
                          type="button"
                          onClick={() => removeBenefit(index)}
                          className="remove-benefit-btn"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="benefit-input-group">
                  <input
                    type="text"
                    value={newBenefit}
                    onChange={(e) => setNewBenefit(e.target.value)}
                    placeholder="Enter a benefit"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addBenefit();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={addBenefit}
                    className="add-benefit-btn"
                  >
                    Add Benefit
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Eligibility *</label>
                <textarea
                  value={formData.eligibility}
                  onChange={(e) => setFormData({ ...formData, eligibility: e.target.value })}
                  placeholder="Enter eligibility criteria"
                  required
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={loading || formData.benefits.length === 0}>
                  {loading ? 'Saving...' : editingScheme ? 'Update Scheme' : 'Add Scheme'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingScheme(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SchemesManager;
